import { apiFetch } from '../../../../utils/api';
import { normalizeCardLayout, normalizeValueSchema, normalizePropertyValues } from '../../../../utils/valuePresentation';

// Feature Selection Handlers
export const handleSelectFeature = (
    setSelectedFeatureId,
    setFriendlyName,
    setDescription,
    setInputType,
    setIsEditingFeatureName,
    setIsEditingValueName,
    setCustomAttrs,
    setFeatureValues,
    setSelectedValueId,
    setExpandedFeatures,
    setExpandedFolders,
    setIsEditMode,
    setValueSchema,
    setIsSystem,
    setCardLayout,
    setIsOutput
) => (v, keepSelectedValueId = null) => {
    if (!v) {
        setSelectedFeatureId(null);
        setSelectedValueId(null);
        return;
    }
    
    setSelectedFeatureId(v.id);
    setFriendlyName((v.property?.displayText !== undefined && v.property?.displayText !== null ? String(v.property.displayText) : '') || v.name);
    setDescription(v.property?.description !== undefined && v.property?.description !== null ? String(v.property.description) : '');
    setInputType((v.property?.type !== undefined && v.property?.type !== null ? String(v.property.type) : '') || 'dropdown');
    setValueSchema?.(normalizeValueSchema(v.property?.valueSchema || v.valueSchema));
    setCardLayout?.(normalizeCardLayout(v.property?.cardLayout || v.property?.CardLayout || v.cardLayout || v.CardLayout));
    setIsSystem?.(Boolean(v.property?.isSystem ?? v.isSystem));
    setIsOutput?.(Boolean(v.property?.isOutput ?? v.isOutput));
    setIsEditingFeatureName(false);
    setIsEditingValueName(false);

    const attrs = v.property?.attributes
        ? Array.isArray(v.property.attributes)
            ? v.property.attributes.map(attr => ({
                key: attr.key !== undefined && attr.key !== null ? String(attr.key) : '',
                value: attr.value !== undefined && attr.value !== null ? String(attr.value) : ''
            }))
            : Object.entries(v.property.attributes).map(([key, value]) => ({ key: String(key), value: String(value) }))
        : [];
    setCustomAttrs(attrs.filter(attr => attr.key !== 'isOutput'));

    const vals = v.values
        ? v.values.map(val => ({
            id: val.id,
            value: val.value !== undefined && val.value !== null ? String(val.value) : '',
            displayText: val.displayText !== undefined && val.displayText !== null ? String(val.displayText) : '',
            description: val.description !== undefined && val.description !== null ? String(val.description) : '',
            imageUrl: val.imageUrl !== undefined && val.imageUrl !== null ? String(val.imageUrl) : '',
            isDefault: Boolean(val.isDefault ?? val.IsDefault),
            sortOrder: Number(val.sortOrder ?? val.SortOrder ?? 0) || 0,
            propertyValues: normalizePropertyValues(val.propertyValues ?? val.PropertyValues),
            attributes: Array.isArray(val.attributes)
                ? val.attributes.map(attr => ({
                    key: attr.key !== undefined && attr.key !== null ? String(attr.key) : '',
                    value: attr.value !== undefined && attr.value !== null ? String(attr.value) : ''
                }))
                : val.attributes
                    ? Object.entries(val.attributes).map(([key, value]) => ({ key: String(key), value: String(value) }))
                    : []
        }))
        : [];
    setFeatureValues(vals);
    setSelectedValueId(keepSelectedValueId);

    setExpandedFeatures(prev => ({
        ...prev,
        [v.id]: true
    }));

    // Automatically expand parent folder paths of the feature path in the tree
    if (v.name && v.name.includes('.')) {
        const parts = v.name.split('.');
        const folderPathsToExpand = [];
        for (let i = 1; i < parts.length; i++) {
            folderPathsToExpand.push(parts.slice(0, i).join('.'));
        }
        if (folderPathsToExpand.length > 0) {
            setExpandedFolders(prev => {
                const next = { ...prev };
                folderPathsToExpand.forEach(path => {
                    next[path] = true;
                });
                return next;
            });
        }
    }
};

// Feature Mapping Handler
export const handleFeatureMappingSubmit = (
    selectedFamilyId,
    mappingFeatureType,
    modalNewFeatureName,
    selectedFeatureToLink,
    features,
    setModalNewFeatureName,
    setIsMappingFeatureModalOpen,
    setMappingSaving,
    fetchData,
    setSelectedFeatureId
) => async (e) => {
    e.preventDefault();
    if (!selectedFamilyId) return;

    setMappingSaving(true);
    try {
        if (mappingFeatureType === 'create') {
            if (!modalNewFeatureName.trim()) return;

            const res = await apiFetch(`/api/features`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Name: modalNewFeatureName.trim(),
                    IsGlobal: false,
                    FamilyId: selectedFamilyId
                })
            });

            if (res.ok) {
                const data = await res.json();
                const newFeatId = data.id || data.Id || data.ID;
                setModalNewFeatureName('');
                setIsMappingFeatureModalOpen(false);
                await fetchData({ selectFeatureId: newFeatId });
            } else {
                alert(`Error creating feature: ${await res.text()}`);
            }
        } else {
            // Link an existing feature
            if (!selectedFeatureToLink) return;

            const targetFeat = features.find(f => f.id === selectedFeatureToLink);
            if (!targetFeat) return;

            const currentFamilyIds = targetFeat.sourceFamilyIds || [];
            const updatedFamilyIds = [...currentFamilyIds, selectedFamilyId];

            const res = await apiFetch(`/api/features/scope/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify([{
                    id: targetFeat.id,
                    isGlobal: targetFeat.isGlobal,
                    mappedFamilyIds: updatedFamilyIds
                }])
            });

            if (res.ok) {
                const linkedFeatId = selectedFeatureToLink;
                setSelectedFeatureToLink('');
                setIsMappingFeatureModalOpen(false);
                await fetchData({ selectFeatureId: linkedFeatId });
            } else {
                alert(`Error linking feature: ${await res.text()}`);
            }
        }
    } catch (err) {
        console.error(err);
    } finally {
        setMappingSaving(false);
    }
};

// Tree Handlers
export const toggleFeature = (expandedFeatures, setExpandedFeatures) => (featureId) => {
    setExpandedFeatures(prev => ({
        ...prev,
        [featureId]: !(prev[featureId] !== false)
    }));
};

export const toggleFolder = (expandedFolders, setExpandedFolders) => (folderPath) => {
    setExpandedFolders(prev => ({
        ...prev,
        [folderPath]: !(prev[folderPath] !== false)
    }));
};

// Feature Delete Handler
export const handleDeleteFeature = (
    selectedFeatureId,
    features,
    setSelectedFeatureId,
    setSavingFeature,
    fetchData,
    confirmDelete
) => async () => {
    if (!selectedFeatureId) return;
    const feat = features.find(f => f.id === selectedFeatureId);
    if (!feat) return;
    if (!await confirmDelete({ kind: 'feature', featureId: selectedFeatureId, name: feat.name })) return;

    try {
        setSavingFeature(true);
        const res = await apiFetch(`/api/features/${selectedFeatureId}`, {
            method: 'DELETE'
        });
        if (res.ok) {
            setSelectedFeatureId(null);
            await fetchData();
        } else {
            alert(`Failed to delete feature: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    } finally {
        setSavingFeature(false);
    }
};
